<template>
  <CSidebar
    fixed
    :minimize="minimize"
    :show="show"
    @update:show="(value) => $store.commit('set', ['sidebarShow', value])"
  >
    <div ref="orgSwitcher" class="org-switcher d-md-down-none">
      <button
        type="button"
        class="org-switcher__trigger"
        :class="{
          'org-switcher__trigger--open': dropdownOpen,
          'org-switcher__trigger--static': !canSwitchOrganisation,
        }"
        :disabled="isUpdatingOrganisation || organisations.length === 0 || !canSwitchOrganisation"
        :aria-expanded="canSwitchOrganisation ? (dropdownOpen ? 'true' : 'false') : null"
        :aria-haspopup="canSwitchOrganisation ? 'listbox' : null"
        @click="toggleDropdown"
      >
        <span class="org-switcher__avatar">
          <span
            v-if="isUpdatingOrganisation"
            class="org-switcher__skeleton org-switcher__skeleton--avatar"
            aria-hidden="true"
          />
          <template v-else>
            <img
              v-if="activeOrganisationBrandImage"
              :src="activeOrganisationBrandImage"
              :alt="activeOrganisationName"
            />
            <span v-else class="org-switcher__initials">
              {{ activeOrganisationInitials }}
            </span>
          </template>
        </span>
        <span class="org-switcher__info">
          <template v-if="isUpdatingOrganisation">
            <span class="org-switcher__skeleton org-switcher__skeleton--name" aria-hidden="true"/>
            <span class="org-switcher__skeleton org-switcher__skeleton--label" aria-hidden="true"/>
          </template>
          <template v-else>
            <span class="org-switcher__name">
              {{ activeOrganisationName || 'Select organisation' }}
            </span>
            <span class="org-switcher__label">
              {{ isAllOrganisationsRoute ? 'AGGREGATED ACROSS ENTITIES' : 'THE SOLUTION BY THE CREATOR' }}
            </span>
          </template>
        </span>
        <!-- <svg
          v-else-if="canSwitchOrganisation"
          class="org-switcher__chevron"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg> -->
      </button>

      <transition name="org-switcher-fade">
        <div
          v-if="dropdownOpen && canSwitchOrganisation"
          class="org-switcher__menu"
          role="listbox"
        >
          <div class="org-switcher__menu-head">Switch organisation</div>
          <ul class="org-switcher__menu-list">
            <li
              v-for="organisation in visibleOrganisations"
              :key="organisation.id"
              role="option"
              :aria-selected="organisation.id === activeOrganisationId ? 'true' : 'false'"
            >
              <button
                type="button"
                class="org-switcher__item"
                :class="{ 'org-switcher__item--active': organisation.id === activeOrganisationId }"
                @click="selectOrganisation(organisation.id)"
              >
                <span class="org-switcher__avatar org-switcher__avatar--sm">
                  <img
                    v-if="organisationImage(organisation)"
                    :src="organisationImage(organisation)"
                    :alt="organisation.name"
                  />
                  <span v-else class="org-switcher__initials">
                    {{ initialsFor(organisation.name) }}
                  </span>
                </span>
                <span class="org-switcher__item-name">{{ organisation.name }}</span>
                <svg
                  v-if="organisation.id === activeOrganisationId"
                  class="org-switcher__check"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </button>
            </li>
          </ul>
        </div>
      </transition>
    </div>

    <CRenderFunction flat :content-to-render="nav"/>
    <CSidebarMinimizer
      class="d-md-down-none"
      @click.native="$store.commit('set', ['sidebarMinimize', !minimize])"
    />
  </CSidebar>
</template>

<script>
import axios from 'axios'
import { getSidebarForRoles, withUserNavExtras } from '@/config/roleSidebarMap'
import { getStoredRoles } from '@/auth/access'
import { resetCompletion } from '@/utils/profileCompletion'
import { normalizeNavIcon } from '@/config/navIcons'

export default {
  name: 'TheSidebar',
  data() {
    return {
      nav: [],
      buffor: [],
      selectedOrganisationId: null,
      isUpdatingOrganisation: false,
      dropdownOpen: false,
    }
  },
  computed: {
    show() {
      return this.$store.state.sidebarShow
    },
    minimize() {
      return this.$store.state.sidebarMinimize
    },
    organisations() {
      return this.$store.state.organisations
    },
    visibleOrganisations() {
      const allowed = new Set([1, 2, 4])
      return this.organisations.filter((org) => allowed.has(Number(org.id)))
    },
    organisationsLoaded() {
      return this.$store.state.organisationsLoaded
    },
    activeOrganisationId() {
      return this.$store.state.activeOrganisationId
    },
    activeOrganisation() {
      return this.$store.getters.activeOrganisation
    },
    activeOrganisationName() {
      if (this.isAllOrganisationsRoute) {
        return 'All Organisations'
      }

      const displayName = this.$store.getters.activeOrganisationDisplayName
      if (displayName) {
        return displayName
      }

      return ''
    },
    activeOrganisationBrandImage() {
      if (this.isAllOrganisationsRoute) {
        return '/MR-Logo.png'
      }
      return this.organisationImage(this.activeOrganisation)
    },
    activeOrganisationInitials() {
      if (this.isAllOrganisationsRoute) {
        return 'ALL'
      }
      return this.initialsFor(this.activeOrganisationName)
    },
    permissions() {
      return this.$store.state.permissions
    },
    isAllOrganisationsRoute() {
      if (
        this.$route.path === '/orders'
        && (this.$route.query.tab === 'all' || this.$route.query.tab === 'search')
      ) {
        return true
      }
      return this.$route.path === '/analytics' || this.$route.path === '/shipping/dashboard' || this.$route.path === '/websites' || this.$route.path === '/pos' || this.$route.path === '/hms_imports' || this.$route.path === '/tele_sales_accounts' || this.$route.path === '/incentive_preview' || this.$route.path === '/inbox'
    },
    canSwitchOrganisation() {
      if (this.isAllOrganisationsRoute) {
        return false
      }
      return this.$store.getters.can('switch_org') && this.visibleOrganisations.length > 1
    },
  },
  watch: {
    activeOrganisationId: {
      immediate: true,
      handler(value, previousValue) {
        this.selectedOrganisationId = value
        if (previousValue !== undefined && value !== previousValue) {
          this.fetchMenu()
        }
      },
    },
    permissions: {
      deep: true,
      handler() {
        this.fetchMenu()
      },
    },
  },
  methods: {
    normalizeIcon(icon) {
      return normalizeNavIcon(icon)
    },
    isAllowed(item) {
      if (!item) {
        return true
      }
      if (Array.isArray(item.organisationIds) && item.organisationIds.length) {
        const activeId = Number(this.$store.state.activeOrganisationId || 0)
        if (!item.organisationIds.map(Number).includes(activeId)) {
          return false
        }
      }
      if (item.permission && !this.$store.getters.can(item.permission)) {
        return false
      }
      return true
    },
    filterByPermission(items) {
      if (!Array.isArray(items)) {
        return []
      }

      return items
        .filter((item) => this.isAllowed(item))
        .map((item) => {
          if (item.slug === 'dropdown' && Array.isArray(item.elements)) {
            const elements = this.filterByPermission(item.elements)
            return elements.length > 0 ? { ...item, elements } : null
          }
          return item
        })
        .filter(Boolean)
    },
    dropdown(data) {
      let result = {
        _name: 'CSidebarNavDropdown',
        name: data.name,
        route: data.href,
        icon: this.normalizeIcon(data.icon),
        _children: [],
      }
      for (let i = 0; i < data.elements.length; i++) {
        if (data.elements[i].slug === 'dropdown') {
          result._children.push(this.dropdown(data.elements[i]))
        } else {
          result._children.push({
            _name: 'CSidebarNavItem',
            name: data.elements[i].name,
            to: data.elements[i].href,
            icon: this.normalizeIcon(data.elements[i].icon)
          })
        }
      }
      return result
    },
    // Section names the user has collapsed, persisted so the preference
    // survives reloads and menu rebuilds (org switches refetch the menu).
    collapsedSections() {
      try {
        const raw = localStorage.getItem('sidebar_collapsed_sections')
        const list = raw ? JSON.parse(raw) : []
        return Array.isArray(list) ? list : []
      } catch (e) {
        return []
      }
    },
    onSectionToggle(name, open) {
      const collapsed = this.collapsedSections().filter((n) => n !== name)
      if (!open) {
        collapsed.push(name)
      }
      try {
        localStorage.setItem('sidebar_collapsed_sections', JSON.stringify(collapsed))
      } catch (e) {
        // Storage unavailable — the section still toggles, it just won't persist.
      }
    },
    rebuildData(data) {
      const collapsed = this.collapsedSections()
      this.buffor = [{
        _name: 'CSidebarNav',
        _children: []
      }]
      const rootChildren = this.buffor[0]._children
      // Each `slug: 'title'` entry becomes a collapsible section holding
      // every item until the next title; items before any title stay at
      // the root. `.nav-section` restyles the dropdown toggle to look
      // like the old static CSidebarNavTitle.
      let section = null
      const push = (node) => (section ? section._children : rootChildren).push(node)
      for (let k = 0; k < data.length; k++) {
        switch (data[k].slug) {
          case 'link':
            if (data[k].href.indexOf('http') !== -1) {
              push({
                _name: 'CSidebarNavItem',
                name: data[k].name,
                href: data[k].href,
                icon: this.normalizeIcon(data[k].icon),
                target: '_blank'
              })
            } else {
              push({
                _name: 'CSidebarNavItem',
                name: data[k].name,
                to: data[k].href,
                icon: this.normalizeIcon(data[k].icon),
              })
            }
            break
          case 'title': {
            const name = data[k].name
            section = {
              _name: 'CSidebarNavDropdown',
              _class: 'nav-section',
              name,
              show: !collapsed.includes(name),
              _on: { 'update:show': (open) => this.onSectionToggle(name, open) },
              _children: []
            }
            rootChildren.push(section)
            break
          }
          case 'dropdown':
            push(this.dropdown(data[k]))
            break
        }
      }
      // Drop sections whose items were all permission-filtered away, and
      // give each remaining one its first child's icon — only visible on
      // the minimized rail so collapsed-sidebar rows aren't blank.
      this.buffor[0]._children = rootChildren.filter((node) => {
        if (node._class !== 'nav-section') {
          return true
        }
        if (!node._children.length) {
          return false
        }
        node.icon = node._children[0].icon || 'cil-list'
        return true
      })
      return this.buffor
    },
    currentUserId() {
      try {
        const raw = localStorage.getItem('auth_user')
        const user = raw ? JSON.parse(raw) : null
        const id = user ? Number.parseInt(user.id, 10) : NaN
        return Number.isInteger(id) ? id : null
      } catch (e) {
        return null
      }
    },
    // Per-user-only nav items live in roleSidebarMap so the spotlight catalog
    // builds from the exact same list.
    withUserExtras(sidebar) {
      return withUserNavExtras(sidebar, this.currentUserId())
    },
    fetchMenu() {
      const roles = getStoredRoles().join(',')
      const sidebar = getSidebarForRoles(roles)

      if (Array.isArray(sidebar) && sidebar.length > 0) {
        this.nav = this.rebuildData(this.filterByPermission(this.withUserExtras(sidebar)))
        return
      }

      let self = this

      axios.get(this.$apiAdress + '/api/menu')
        .then(function (response) {
          self.nav = self.rebuildData(self.filterByPermission(self.withUserExtras(response.data)))
        }).catch(function (error) {
          console.log(error)
          self.clearAuthState()
          self.$router.push({ path: '/login' })
        })
    },
    fetchOrganisations() {
      this.$store.dispatch('fetchOrganisations').catch(function (error) {
        console.log(error)
      })
    },
    imageForName(name) {
      const lower = (name || '').toLowerCase()

      if (lower.includes('agro')) {
        return '/MCCI-Agro.png'
      }

      if (lower.includes('drug')) {
        return '/MCCI-Drugs.png'
      }

      return null
    },
    organisationImage(organisation) {
      if (organisation && typeof organisation.logo_url === 'string' && organisation.logo_url.trim()) {
        return organisation.logo_url.trim()
      }
      return this.imageForName(organisation && organisation.name)
    },
    initialsFor(name) {
      if (!name) {
        return '?'
      }

      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('')
    },
    toggleDropdown() {
      if (
        this.isUpdatingOrganisation
        || this.organisations.length === 0
        || !this.canSwitchOrganisation
      ) {
        return
      }
      this.dropdownOpen = !this.dropdownOpen
    },
    closeDropdown() {
      this.dropdownOpen = false
    },
    selectOrganisation(id) {
      this.dropdownOpen = false
      if (Number(id) === Number(this.activeOrganisationId)) {
        return
      }
      this.selectedOrganisationId = id
      this.handleOrganisationChange()
    },
    handleDocumentClick(event) {
      const switcher = this.$refs.orgSwitcher
      if (!switcher || switcher.contains(event.target)) {
        return
      }
      this.closeDropdown()
    },
    handleEscape(event) {
      if (event.key === 'Escape') {
        this.closeDropdown()
      }
    },
    handleOrganisationChange() {
      if (this.isUpdatingOrganisation) {
        return
      }

      const nextOrgId = Number(this.selectedOrganisationId)
      const previousOrgId = this.activeOrganisationId
      const previousOrg = this.activeOrganisation

      if (!nextOrgId || nextOrgId === previousOrgId) {
        return
      }

      this.isUpdatingOrganisation = true
      const nextOrg = this.organisations.find((organisation) => Number(organisation.id) === nextOrgId) || null
      this.$store.commit('setActiveOrganisationContext', {
        id: nextOrgId,
        name: nextOrg ? nextOrg.name : '',
      })

      let self = this

      axios.post(this.$apiAdress + '/api/organisation/change_my_org', {
        organisation_id: nextOrgId,
      }).then(function () {
        self.fetchMenu()
      }).catch(function (error) {
        console.log(error)
        self.$store.commit('setActiveOrganisationContext', {
          id: previousOrgId,
          name: previousOrg ? previousOrg.name : '',
        })
        self.selectedOrganisationId = previousOrgId
      }).finally(function () {
        self.isUpdatingOrganisation = false
      })
    },
    clearAuthState() {
      localStorage.removeItem('api_token')
      localStorage.removeItem('roles')
      localStorage.removeItem('auth_user')
      resetCompletion()
      this.$store.commit('clearOrganisationContext')
      this.$store.commit('clearPermissions')
    }
  },
  mounted() {
    this.$root.$on('toggle-sidebar', () => {
      const sidebarOpened = this.show === true || this.show === 'responsive'
      this.show = sidebarOpened ? false : 'responsive'
    })
    this.$root.$on('toggle-sidebar-mobile', () => {
      const sidebarClosed = this.show === 'responsive' || this.show === false
      this.show = sidebarClosed ? true : 'responsive'
    })

    if (!this.organisationsLoaded) {
      this.fetchOrganisations()
    }
    this.fetchMenu()

    document.addEventListener('mousedown', this.handleDocumentClick)
    document.addEventListener('keydown', this.handleEscape)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleDocumentClick)
    document.removeEventListener('keydown', this.handleEscape)
  }
}
</script>

<style scoped>
.c-sidebar ::v-deep .c-sidebar-nav,
.c-sidebar ::v-deep .ps,
.c-sidebar ::v-deep .ps__rail-y {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.16) transparent;
}

.c-sidebar ::v-deep .c-sidebar-nav::-webkit-scrollbar,
.c-sidebar ::v-deep .ps::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.c-sidebar ::v-deep .c-sidebar-nav::-webkit-scrollbar-track,
.c-sidebar ::v-deep .ps::-webkit-scrollbar-track {
  background: transparent;
}

.c-sidebar ::v-deep .c-sidebar-nav::-webkit-scrollbar-thumb,
.c-sidebar ::v-deep .ps::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  transition: background-color 0.15s ease;
}

.c-sidebar ::v-deep .c-sidebar-nav:hover::-webkit-scrollbar-thumb,
.c-sidebar ::v-deep .ps:hover::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.24);
}

.c-sidebar ::v-deep .c-sidebar-nav::-webkit-scrollbar-thumb:hover,
.c-sidebar ::v-deep .ps::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.36);
}

.c-sidebar ::v-deep .ps__rail-x,
.c-sidebar ::v-deep .ps__rail-y {
  background-color: transparent !important;
  opacity: 1 !important;
}

.c-sidebar ::v-deep .ps__thumb-y {
  background-color: rgba(255, 255, 255, 0.18) !important;
  width: 4px !important;
  right: 1px !important;
  border-radius: 999px !important;
}

.c-sidebar ::v-deep .ps__rail-y:hover .ps__thumb-y,
.c-sidebar ::v-deep .ps__rail-y.ps--clicking .ps__thumb-y {
  background-color: rgba(255, 255, 255, 0.34) !important;
  width: 5px !important;
}

/* Brand-green nav hover/active — overrides CoreUI's default blue */
.c-sidebar ::v-deep .c-sidebar-nav-link:hover,
.c-sidebar ::v-deep .c-sidebar-nav-dropdown-toggle:hover {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%) !important;
  color: #ffffff !important;
}

.c-sidebar ::v-deep .c-sidebar-nav-link:hover .c-sidebar-nav-icon,
.c-sidebar ::v-deep .c-sidebar-nav-dropdown-toggle:hover .c-sidebar-nav-icon {
  color: #ffffff !important;
}

.c-sidebar ::v-deep .c-sidebar-nav-link.c-active,
.c-sidebar ::v-deep .c-active.c-sidebar-nav-dropdown-toggle {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%) !important;
  color: #ffffff !important;
}

.c-sidebar ::v-deep .c-sidebar-nav-link.c-active .c-sidebar-nav-icon,
.c-sidebar ::v-deep .c-active.c-sidebar-nav-dropdown-toggle .c-sidebar-nav-icon {
  color: #ffffff !important;
}

.c-sidebar ::v-deep .c-sidebar-nav-dropdown.c-show > .c-sidebar-nav-dropdown-toggle {
  background: rgba(22, 160, 122, 0.18) !important;
}

.c-sidebar ::v-deep .c-sidebar-nav-dropdown.c-show .c-sidebar-nav-dropdown-items {
  background: rgba(0, 0, 0, 0.14);
}

/* Collapsed sidebar fly-out: replace blue hover backdrop with brand green */
.c-sidebar-minimized ::v-deep .c-sidebar-nav-dropdown:hover {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%) !important;
}

.c-sidebar-minimized ::v-deep .c-sidebar-nav-dropdown:hover > .c-sidebar-nav-dropdown-items {
  background: #2a3a35 !important;
  border: 1px solid rgba(22, 160, 122, 0.25);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
  border-radius: 0 8px 8px 0;
  overflow: hidden;
}

.c-sidebar-minimized ::v-deep .c-sidebar-nav-dropdown:hover > .c-sidebar-nav-dropdown-toggle {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%) !important;
  color: #ffffff !important;
}

/* ── Collapsible title-sections ──
   rebuildData renders each `slug: 'title'` entry as a CSidebarNavDropdown
   with the .nav-section class so the whole section can collapse. These
   rules restyle its toggle to look like the old static CSidebarNavTitle
   (small uppercase muted label) instead of a nav link. */
.c-sidebar ::v-deep .nav-section {
  margin-top: 0.5rem;
}

.c-sidebar ::v-deep .nav-section > .c-sidebar-nav-dropdown-toggle {
  padding-top: 0.55rem;
  padding-bottom: 0.55rem;
  font-size: 80%;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.55);
  background: transparent !important;
}

/* Keep the muted title look while the section is open (overrides the
   green .c-show tint meant for real dropdowns). */
.c-sidebar ::v-deep .nav-section.c-show > .c-sidebar-nav-dropdown-toggle {
  background: transparent !important;
}

/* Subtle hover instead of the brand-gradient nav-link hover. Declared
   after the .c-show rule above so hovering an open section still reads. */
.c-sidebar ::v-deep .nav-section > .c-sidebar-nav-dropdown-toggle:hover {
  background: rgba(255, 255, 255, 0.05) !important;
  color: rgba(255, 255, 255, 0.85) !important;
}

/* No darker inset band behind an open section — only real (nested)
   dropdowns keep it. */
.c-sidebar ::v-deep .nav-section.c-show > .c-sidebar-nav-dropdown-items {
  background: transparent;
}

/* Section children align like top-level links (CoreUI indents dropdown
   items by default); dropdowns nested inside a section keep their own
   deeper indent for their items. */
.c-sidebar ::v-deep .nav-section > .c-sidebar-nav-dropdown-items > .c-sidebar-nav-item > .c-sidebar-nav-link,
.c-sidebar ::v-deep .nav-section > .c-sidebar-nav-dropdown-items > .c-sidebar-nav-dropdown > .c-sidebar-nav-dropdown-toggle {
  padding-left: 1rem;
}

/* CoreUI pairs that 56px indent with a -56px margin on the icon; with the
   padding reset above that margin would drag icons out of view, so restore
   the top-level -1rem offset. */
.c-sidebar ::v-deep .nav-section > .c-sidebar-nav-dropdown-items > .c-sidebar-nav-item > .c-sidebar-nav-link > .c-sidebar-nav-icon,
.c-sidebar ::v-deep .nav-section > .c-sidebar-nav-dropdown-items > .c-sidebar-nav-dropdown > .c-sidebar-nav-dropdown-toggle > .c-sidebar-nav-icon {
  margin-left: -1rem;
}

/* The section icon (borrowed from its first child) is only for the
   minimized rail — hide it while the sidebar is expanded. */
.c-sidebar:not(.c-sidebar-minimized) ::v-deep .nav-section > .c-sidebar-nav-dropdown-toggle > .c-sidebar-nav-icon {
  display: none;
}

.org-switcher {
  position: relative;
  padding: 0.6rem 0.6rem 0.55rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

/* ── Trigger ── */
.org-switcher__trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.6rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 10px;
  color: #ffffff;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.org-switcher__trigger:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
}

/* Suppress the UA outline on every focus path; the brand-green ring
   below only renders for keyboard focus via :focus-visible. */
.org-switcher__trigger:focus {
  outline: none;
}

.org-switcher__trigger::-moz-focus-inner {
  border: 0;
}

.org-switcher__trigger:focus-visible {
  outline: none;
  background: rgba(255, 255, 255, 0.05);
  /* Brighter brand-green tint than the light-page default — needed
     so the ring still reads against the dark sidebar background. */
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.55);
}

.org-switcher__trigger--open {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.08);
}

.org-switcher__trigger:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Read-only mode: user cannot switch organisations.
   Render the active org card normally (no faded/disabled look, no hover affordance). */
.org-switcher__trigger--static,
.org-switcher__trigger--static:disabled {
  cursor: default;
  opacity: 1;
}

.org-switcher__trigger--static:hover,
.org-switcher__trigger--static:focus-visible {
  background: transparent;
  border-color: transparent;
}

.org-switcher__trigger--static:hover .org-switcher__avatar {
  transform: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

/* ── Avatar ── */
.org-switcher__avatar {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: transform 0.15s ease, 
}

.org-switcher__trigger:hover:not(:disabled) .org-switcher__avatar,
.org-switcher__trigger--open .org-switcher__avatar {
  transform: translateY(-1px);
}

.org-switcher__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.org-switcher__avatar--sm {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  box-shadow: none;
  transform: none !important;
}

.org-switcher__initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  font-size: 1.05rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.04em;
}

.org-switcher__avatar--sm .org-switcher__initials {
  font-size: 0.74rem;
  letter-spacing: 0.02em;
}

/* ── Trigger text ── */
.org-switcher__info {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.org-switcher__label {
  font-size: 0.45rem;
  font-weight: 700;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.42);
}

.org-switcher__name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #ffffff;
  margin-top: 3px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.org-switcher__chevron {
  flex: 0 0 auto;
  color: rgba(255, 255, 255, 0.45);
  transition: transform 0.18s ease, color 0.15s ease;
}

.org-switcher__trigger:hover .org-switcher__chevron,
.org-switcher__trigger--open .org-switcher__chevron {
  color: rgba(255, 255, 255, 0.8);
}

.org-switcher__trigger--open .org-switcher__chevron {
  transform: rotate(180deg);
}

.org-switcher__skeleton {
  display: block;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.06) 0%,
    rgba(255, 255, 255, 0.18) 50%,
    rgba(255, 255, 255, 0.06) 100%
  );
  background-size: 200% 100%;
  border-radius: 4px;
  animation: org-switcher-shimmer 1.4s ease-in-out infinite;
}

.org-switcher__skeleton--avatar {
  width: 100%;
  height: 100%;
  border-radius: inherit;
}

.org-switcher__skeleton--name {
  width: 70%;
  height: 0.7rem;
  margin-top: 4px;
}

.org-switcher__skeleton--label {
  width: 45%;
  height: 0.4rem;
  margin-top: 6px;
}

@keyframes org-switcher-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ── Menu ── */
.org-switcher__menu {
  position: absolute;
  top: calc(100% + 0.3rem);
  left: 0.6rem;
  right: 0.6rem;
  z-index: 1050;
  margin: 0;
  padding: 0;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 320px;
}

.org-switcher__menu-head {
  padding: 0.55rem 0.7rem 0.45rem;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #6b7280;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
}

.org-switcher__menu-list {
  margin: 0;
  padding: 0.3rem;
  list-style: none;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.org-switcher__menu-list::-webkit-scrollbar {
  width: 5px;
}

.org-switcher__menu-list::-webkit-scrollbar-track {
  background: transparent;
}

.org-switcher__menu-list::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 999px;
}

.org-switcher__menu-list::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}

/* ── Menu items ── */
.org-switcher__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.55rem;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: #1f2a44;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.12s ease, color 0.12s ease;
}

.org-switcher__item:hover {
  background: #f5fbf8;
  color: #0e5b46;
}

.org-switcher__item:focus {
  outline: none;
}

.org-switcher__item::-moz-focus-inner {
  border: 0;
}

.org-switcher__item:focus-visible {
  outline: none;
  background: #f5fbf8;
  color: #0e5b46;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.org-switcher__item--active {
  background: #dff5ea;
  color: #0e5b46;
}

.org-switcher__item--active:hover,
.org-switcher__item--active:focus-visible {
  background: #cdeede;
  color: #0e5b46;
}

.org-switcher__item-name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.84rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.org-switcher__item--active .org-switcher__item-name {
  font-weight: 700;
}

.org-switcher__check {
  flex: 0 0 auto;
  color: #16a07a;
}

/* ── Transition ── */
.org-switcher-fade-enter-active,
.org-switcher-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.org-switcher-fade-enter,
.org-switcher-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Collapsed sidebar: shrink to a centred avatar only */
.c-sidebar-minimized .org-switcher {
  padding: 0.45rem 0.35rem;
}

.c-sidebar-minimized .org-switcher__trigger {
  justify-content: center;
  padding: 0.25rem;
  gap: 0;
}

.c-sidebar-minimized .org-switcher__info,
.c-sidebar-minimized .org-switcher__chevron {
  display: none;
}

/* Shrink the avatar so it sits comfortably inside the narrow
   collapsed-sidebar rail instead of pressing against its edges. */
.c-sidebar-minimized .org-switcher__avatar {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}

.c-sidebar-minimized .org-switcher__avatar .org-switcher__initials {
  font-size: 0.78rem;
  letter-spacing: 0.02em;
}

.c-sidebar-minimized .org-switcher__trigger:hover:not(:disabled) .org-switcher__avatar,
.c-sidebar-minimized .org-switcher__trigger--open .org-switcher__avatar {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3), inset 0 0 0 1px rgba(22, 160, 122, 0.35);
}

.c-sidebar-minimized .org-switcher__menu {
  left: 100%;
  right: auto;
  top: 0.6rem;
  min-width: 220px;
  margin-left: 0.4rem;
}
</style>
